import { MONTHS, type YearlyRevenue } from '@vy/types';

export type MonthColumn = { year: string; month: (typeof MONTHS)[number]; label: string };

type ContractWindow = { startDate?: Date | string | null; endDate?: Date | string | null };

type AbsMonth = number;

const MONTHS_IN_YEAR = 12;
const FALLBACK_WINDOW_MONTHS = 12;

function toAbsMonth(year: number, monthIndex: number): AbsMonth {
  return year * MONTHS_IN_YEAR + monthIndex;
}

function fromAbsMonth(abs: AbsMonth): { year: number; monthIndex: number } {
  return { year: Math.floor(abs / MONTHS_IN_YEAR), monthIndex: abs % MONTHS_IN_YEAR };
}

function parseMonth(value: Date | string | null | undefined): AbsMonth | null {
  if (value === null || value === undefined) return null;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return toAbsMonth(date.getUTCFullYear(), date.getUTCMonth());
}

function dataMonths(yearlyRevenue: YearlyRevenue): AbsMonth[] {
  const months: AbsMonth[] = [];
  for (const year of Object.keys(yearlyRevenue)) {
    const monthsForYear = yearlyRevenue[year];
    if (!monthsForYear) continue;
    for (const month of MONTHS) {
      if (monthsForYear[month]) {
        months.push(toAbsMonth(Number(year), MONTHS.indexOf(month)));
      }
    }
  }
  return months.sort((a, b) => a - b);
}

function toColumn(abs: AbsMonth): MonthColumn {
  const { year, monthIndex } = fromAbsMonth(abs);
  const month = MONTHS[monthIndex];
  const yearStr = String(year);
  return { year: yearStr, month, label: `${month.slice(0, 3)} ${yearStr}` };
}

export function buildMonthColumns(
  yearlyRevenue: YearlyRevenue,
  contract: ContractWindow | undefined,
): MonthColumn[] {
  const windowStart = parseMonth(contract?.startDate);
  const months = dataMonths(yearlyRevenue);

  if (windowStart === null) {
    return months.map(toColumn);
  }

  const windowEnd = parseMonth(contract?.endDate) ?? windowStart + FALLBACK_WINDOW_MONTHS - 1;
  const dataStart = months[0];
  const dataEnd = months[months.length - 1];
  const rangeStart = dataStart !== undefined ? Math.min(windowStart, dataStart) : windowStart;
  const rangeEnd = dataEnd !== undefined ? Math.max(windowEnd, dataEnd) : windowEnd;

  const columns: MonthColumn[] = [];

  for (let abs = rangeStart; abs <= rangeEnd; abs++) {
    columns.push(toColumn(abs));
  }

  return columns;
}
